import React from 'react';
import { CalendarProps } from '../types/Calendar.types';
import { CalendarHeader } from '../molecules/CalendarHeader';
import { CalendarGrid } from '../molecules/CalendarGrid';
import { MonthGrid } from '../molecules/MonthGrid';
import { YearGrid } from '../molecules/YearGrid';
import { InnerButton } from '@/modules/InnerButton';
import { cn } from '@/shared/utils';

export const Calendar: React.FC<CalendarProps> = ({
  currentDate,
  selectedDate,
  selectedRange,
  onDateSelect,
  onDateHover,
  minDate,
  maxDate,
  viewMode,
  onViewModeChange,
  onNavigate,
  onMonthSelect,
  onYearSelect,
  onGoToToday,
  locale = 'es-ES',
  weekDays = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
  monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  startOfWeek = 1,
  showWeekNumbers = false,
  yearRange = 12,
  hoverDate,
  closeOnSelect,
  autoCloseRange,
  onAccept,
  onCancel,
  renderHeader,
  renderDay,
}) => {
  const handleNavigate = (direction: 'prev' | 'next') => {
    onNavigate?.(direction);
  };

  const handleToggleViewMode = () => {
    switch (viewMode) {
      case 'days':
        onViewModeChange('months');
        break;
      case 'months':
        onViewModeChange('years');
        break;
      case 'years':
        onViewModeChange('days');
        break;
    }
  };

  const handleMonthSelect = (monthIndex: number) => {
    onMonthSelect?.(monthIndex);
  };

  const handleYearSelect = (year: number) => {
    onYearSelect?.(year);
  };

  const renderView = () => {
    switch (viewMode) {
      case 'days':
        return (
          <CalendarGrid
            currentDate={currentDate}
            selectedDate={selectedDate}
            selectedRange={selectedRange}
            onDateSelect={onDateSelect}
            onDateHover={onDateHover}
            minDate={minDate}
            maxDate={maxDate}
            weekDays={weekDays}
            startOfWeek={startOfWeek}
            showWeekNumbers={showWeekNumbers}
            hoverDate={hoverDate}
            renderDay={renderDay}
          />
        );
      case 'months':
        return (
          <MonthGrid
            currentDate={currentDate}
            selectedMonth={currentDate.getMonth()}
            onMonthSelect={handleMonthSelect}
            monthNames={monthNames}
          />
        );
      case 'years':
        return (
          <YearGrid
            currentDate={currentDate}
            selectedYear={currentDate.getFullYear()}
            onYearSelect={handleYearSelect}
            yearRange={yearRange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'ui-min-w-[254px] ui-overflow-hidden ui-rounded-lg ui-bg-fill ui-shadow-04',
        closeOnSelect ? 'ui-min-h-[280px]' : 'ui-min-h-[325px]'
      )}
    >
      <CalendarHeader
        currentDate={currentDate}
        viewMode={viewMode}
        onNavigate={handleNavigate}
        onToggleViewMode={handleToggleViewMode}
        onGoToToday={onGoToToday}
        monthNames={monthNames}
        renderHeader={renderHeader}
      />
      <div className="ui-h-full ui-p-16">{renderView()}</div>
      {(!closeOnSelect || (selectedRange && !autoCloseRange)) && (
        <footer className="ui-flex ui-justify-end ui-gap-8 ui-border-t ui-border-default ui-p-3">
          <InnerButton size="xs" pill="sm" color="secondary" onClick={onCancel}>
            Cancelar
          </InnerButton>
          <InnerButton size="xs" pill="sm" onClick={onAccept}>
            Aceptar
          </InnerButton>
        </footer>
      )}
    </div>
  );
};
